import React, { useEffect } from 'react';
import { ArrowLeft, CheckCircle, Award, FileCheck, Users, Shield, Target, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const CMMCReadinessPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="dark-container">
      <Header />
      
      {/* Hero Section */}
      <section className="service-hero">
        <div className="dark-content-container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          
          <div className="service-hero-content">
            <div className="service-badge">
              <CheckCircle className="service-badge-icon" />
              <span>CMMC Readiness & Audit Support</span>
            </div>
            
            <h1 className="service-title">
              Defense Contractor 
              <span className="service-title-accent"> CMMC Certification</span>
            </h1>
            
            <p className="service-description">
              Comprehensive CMMC 2.0 readiness assessment and certification support for defense 
              contractors. Ensure your organization meets all Cybersecurity Maturity Model 
              Certification requirements for DoD contract eligibility.
            </p>
            
            <div className="service-stats">
              <div className="service-stat">
                <span className="service-stat-number">100%</span>
                <span className="service-stat-label">First-Time Certification Rate</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">180</span>
                <span className="service-stat-label">Days Average Preparation Time</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">50+</span>
                <span className="service-stat-label">Successful CMMC Certifications</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CMMC Levels Section */}
      <section className="cmmc-levels-section">
        <div className="dark-content-container">
          <h2 className="display-large">CMMC 2.0 Certification Levels</h2>
          
          <div className="cmmc-levels-grid">
            <div className="cmmc-level">
              <div className="level-badge level-1">Level 1</div>
              <h3 className="heading-2">Foundational</h3>
              <p className="body-medium">
                Basic cyber hygiene practices to protect Federal Contract Information (FCI). 
                Self-assessment based with 17 practices across 14 domains.
              </p>
              <ul className="level-features">
                <li>Self-assessment certification</li>
                <li>17 safeguarding practices</li>
                <li>Annual affirmation required</li>
                <li>Federal Contract Information protection</li>
              </ul>
              <div className="level-timeline">
                <Clock size={16} />
                <span>30-60 Days Preparation</span>
              </div>
            </div>

            <div className="cmmc-level">
              <div className="level-badge level-2">Level 2</div>
              <h3 className="heading-2">Advanced</h3>
              <p className="body-medium">
                Enhanced cybersecurity practices for Controlled Unclassified Information (CUI). 
                Third-party assessment required with 110 practices across 14 domains.
              </p>
              <ul className="level-features">
                <li>Third-party assessment required</li>
                <li>110 security practices</li>
                <li>Triennial assessment cycle</li>
                <li>CUI protection requirements</li>
              </ul>
              <div className="level-timeline">
                <Clock size={16} />
                <span>180-365 Days Preparation</span>
              </div>
            </div>

            <div className="cmmc-level">
              <div className="level-badge level-3">Level 3</div>
              <h3 className="heading-2">Expert</h3>
              <p className="body-medium">
                Advanced and progressive cybersecurity practices for the highest priority acquisitions. 
                Government-led assessment with enhanced security requirements.
              </p>
              <ul className="level-features">
                <li>Government-led assessment</li>
                <li>Enhanced security requirements</li>
                <li>Critical national security programs</li>
                <li>Advanced persistent threat protection</li>
              </ul>
              <div className="level-timeline">
                <Clock size={16} />
                <span>12+ Months Preparation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Assessment Domains */}
      <section className="domains-section">
        <div className="dark-content-container">
          <h2 className="display-large">CMMC Security Domains</h2>
          
          <div className="domains-grid">
            <div className="domain-card">
              <Shield className="domain-icon" />
              <h3 className="heading-3">Access Control (AC)</h3>
              <p className="body-small">Limit information system access to authorized users, processes, and devices</p>
            </div>

            <div className="domain-card">
              <Award className="domain-icon" />
              <h3 className="heading-3">Awareness & Training (AT)</h3>
              <p className="body-small">Ensure personnel are trained and understand cybersecurity responsibilities</p>
            </div>

            <div className="domain-card">
              <FileCheck className="domain-icon" />
              <h3 className="heading-3">Audit & Accountability (AU)</h3>
              <p className="body-small">Create and retain system audit logs for monitoring and analysis</p>
            </div>

            <div className="domain-card">
              <Users className="domain-icon" />
              <h3 className="heading-3">Configuration Management (CM)</h3>
              <p className="body-small">Establish and maintain baseline configurations and inventories</p>
            </div>

            <div className="domain-card">
              <Target className="domain-icon" />
              <h3 className="heading-3">Identification & Authentication (IA)</h3>
              <p className="body-small">Identify users, processes, and devices before access is granted</p>
            </div>

            <div className="domain-card">
              <CheckCircle className="domain-icon" />
              <h3 className="heading-3">Incident Response (IR)</h3>
              <p className="body-small">Establish operational incident handling capability for effective response</p>
            </div>
          </div>
        </div>
      </section>

      {/* Readiness Methodology */}
      <section className="methodology-section">
        <div className="dark-content-container">
          <h2 className="display-large">CMMC Readiness Process</h2>
          
          <div className="methodology-grid">
            <div className="methodology-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3 className="heading-2">Gap Assessment</h3>
                <p className="body-medium">
                  Comprehensive evaluation of current security posture against CMMC requirements 
                  to identify gaps and prioritize remediation efforts.
                </p>
                <ul className="step-features">
                  <li>Current state security assessment</li>
                  <li>CMMC practice gap analysis</li>
                  <li>Evidence collection planning</li>
                  <li>Remediation roadmap development</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3 className="heading-2">Implementation Planning</h3>
                <p className="body-medium">
                  Detailed implementation plan with timelines, resource allocation, and 
                  milestone tracking to achieve CMMC compliance efficiently.
                </p>
                <ul className="step-features">
                  <li>Control implementation strategy</li>
                  <li>Policy and procedure development</li>
                  <li>Technical control deployment</li>
                  <li>Staff training and awareness</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3 className="heading-2">Evidence Collection</h3>
                <p className="body-medium">
                  Systematic documentation and evidence collection to demonstrate 
                  compliance with all applicable CMMC practices and requirements.
                </p>
                <ul className="step-features">
                  <li>Documentation standardization</li>
                  <li>Evidence repository creation</li>
                  <li>Process validation</li>
                  <li>Continuous monitoring setup</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3 className="heading-2">Pre-Assessment & Certification Support</h3>
                <p className="body-medium">
                  Mock assessments and certification preparation to ensure readiness 
                  for official CMMC evaluation and successful certification.
                </p>
                <ul className="step-features">
                  <li>Mock assessment execution</li>
                  <li>Assessor interaction preparation</li>
                  <li>Final remediation support</li>
                  <li>Certification maintenance planning</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Support */}
      <section className="certification-support-section">
        <div className="dark-content-container">
          <h2 className="display-large">End-to-End Certification Support</h2>
          
          <div className="support-grid">
            <div className="support-content">
              <h3 className="heading-2">Expert Guidance Throughout Your Journey</h3>
              <p className="body-large">
                Our CMMC-certified professionals provide comprehensive support from initial 
                assessment through certification and ongoing compliance maintenance.
              </p>
              
              <div className="support-benefits">
                <div className="support-benefit">
                  <Award className="benefit-icon" />
                  <div>
                    <h4 className="heading-3">Certified CMMC Professionals</h4>
                    <p className="body-small">Team includes Registered Practitioners (RPs) and Certified Assessors (CAs)</p>
                  </div>
                </div>
                
                <div className="support-benefit">
                  <FileCheck className="benefit-icon" />
                  <div>
                    <h4 className="heading-3">Comprehensive Documentation</h4>
                    <p className="body-small">Complete policy templates, procedures, and evidence collection frameworks</p>
                  </div>
                </div>
                
                <div className="support-benefit">
                  <Target className="benefit-icon" />
                  <div>
                    <h4 className="heading-3">Ongoing Maintenance Support</h4>
                    <p className="body-small">Continuous monitoring and compliance maintenance throughout certification period</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="support-stats">
              <div className="stat-item">
                <span className="stat-number">$2.8M</span>
                <span className="stat-label">Average DoD Contract Value Protected</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">99.2%</span>
                <span className="stat-label">Client Satisfaction Rate</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">30+</span>
                <span className="stat-label">Industry Sectors Served</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CMMCReadinessPage;